import type { Coord, MatchGroup, PieceKind, SpecialType } from '../core/types';
import type { Grid } from './grid';

/**
 * Find all match groups on the board.
 * Patterns:
 *  - line of 3+
 *  - 5+ line => prism
 *  - 4 line => beam
 *  - 2x2 square => burst
 *  - T/L intersections => nova
 */
export function findMatches(grid: Grid): MatchGroup[] {
  const visitedLine = new Set<string>();
  const groups: MatchGroup[] = [];
  const cellToGroups = new Map<string, MatchGroup[]>();

  // Horizontal runs
  for (let r = 0; r < grid.rows; r++) {
    let c = 0;
    while (c < grid.cols) {
      const piece = grid.pieceAt(r, c);
      if (!piece || !grid.active(r, c) || isBlocked(grid, r, c)) {
        c++;
        continue;
      }
      const kind = piece.kind;
      let end = c + 1;
      while (end < grid.cols) {
        const p2 = grid.pieceAt(r, end);
        if (!p2 || p2.kind !== kind || !grid.active(r, end) || isBlocked(grid, r, end)) break;
        end++;
      }
      const len = end - c;
      if (len >= 3) {
        const cells: Coord[] = [];
        for (let cc = c; cc < end; cc++) cells.push({ r, c: cc });
        const g = makeLineGroup(cells, kind, 'h');
        groups.push(g);
        for (const cell of cells) {
          const k = `${cell.r},${cell.c}`;
          visitedLine.add(k);
          if (!cellToGroups.has(k)) cellToGroups.set(k, []);
          cellToGroups.get(k)!.push(g);
        }
      }
      c = end;
    }
  }

  // Vertical runs
  for (let c = 0; c < grid.cols; c++) {
    let r = 0;
    while (r < grid.rows) {
      const piece = grid.pieceAt(r, c);
      if (!piece || !grid.active(r, c) || isBlocked(grid, r, c)) {
        r++;
        continue;
      }
      const kind = piece.kind;
      let end = r + 1;
      while (end < grid.rows) {
        const p2 = grid.pieceAt(end, c);
        if (!p2 || p2.kind !== kind || !grid.active(end, c) || isBlocked(grid, end, c)) break;
        end++;
      }
      const len = end - r;
      if (len >= 3) {
        const cells: Coord[] = [];
        for (let rr = r; rr < end; rr++) cells.push({ r: rr, c });
        const g = makeLineGroup(cells, kind, 'v');
        groups.push(g);
        for (const cell of cells) {
          const k = `${cell.r},${cell.c}`;
          visitedLine.add(k);
          if (!cellToGroups.has(k)) cellToGroups.set(k, []);
          cellToGroups.get(k)!.push(g);
        }
      }
      r = end;
    }
  }

  // Detect intersections for nova (cell in both H and V groups of same kind)
  const intersectionCells = new Set<string>();
  for (const [k, gs] of cellToGroups) {
    if (gs.length >= 2) {
      const kinds = new Set(gs.map((g) => g.kind));
      if (kinds.size === 1) {
        intersectionCells.add(k);
        // upgrade one of the groups to nova
        for (const g of gs) {
          if (g.specialToCreate !== 'prism') {
            g.specialToCreate = 'nova';
            g.pattern = 'intersect';
            const [rs, cs] = k.split(',').map(Number);
            g.specialAt = { r: rs!, c: cs! };
          }
        }
      }
    }
  }

  // 2x2 squares for burst
  for (let r = 0; r < grid.rows - 1; r++) {
    for (let c = 0; c < grid.cols - 1; c++) {
      const cells = [
        { r, c },
        { r, c: c + 1 },
        { r: r + 1, c },
        { r: r + 1, c: c + 1 },
      ];
      if (cells.some((p) => !grid.active(p.r, p.c) || isBlocked(grid, p.r, p.c))) continue;
      const pieces = cells.map((p) => grid.pieceAt(p.r, p.c));
      if (pieces.some((p) => !p)) continue;
      const kind = pieces[0]!.kind;
      if (pieces.every((p) => p!.kind === kind)) {
        // Only create burst group if not already fully covered by a larger prism match
        const already = cells.every((p) => {
          const gs = cellToGroups.get(`${p.r},${p.c}`);
          return gs?.some((g) => g.specialToCreate === 'prism');
        });
        if (!already) {
          groups.push({
            cells,
            kind,
            pattern: 'square',
            specialToCreate: 'burst',
            specialAt: { r, c },
          });
        }
      }
    }
  }

  return mergeOverlapping(groups);
}

function isBlocked(grid: Grid, r: number, c: number): boolean {
  const cell = grid.get(r, c);
  if (!cell) return true;
  if (cell.sealed) return true;
  if (cell.blocker?.type === 'crate' && cell.blocker.layers > 0) return true;
  // frost allows matching of pieces on top — frost is under-tile style damage
  return false;
}

function makeLineGroup(cells: Coord[], kind: PieceKind, axis: 'h' | 'v'): MatchGroup {
  const len = cells.length;
  let specialToCreate: SpecialType | undefined;
  let pattern: MatchGroup['pattern'] = 'line';
  if (len >= 5) {
    specialToCreate = 'prism';
    pattern = 'five';
  } else if (len === 4) {
    specialToCreate = 'beam';
    pattern = 'long';
  }
  return {
    cells,
    kind,
    pattern,
    specialToCreate,
    beamAxis: specialToCreate === 'beam' ? axis : undefined,
    specialAt: cells[Math.floor(cells.length / 2)],
  };
}

/** Merge overlapping groups of same kind into combined groups for clear. */
function mergeOverlapping(groups: MatchGroup[]): MatchGroup[] {
  if (groups.length <= 1) return groups;
  // For resolution we return all groups; engine unions cells.
  // Prefer higher special when same cells overlap.
  return groups;
}

/** Union all matched cells with preferred special creation. */
export function resolveMatchPlan(
  groups: MatchGroup[],
  preferredSpecialAt?: Coord
): {
  clearCells: Coord[];
  specials: Array<{ at: Coord; special: SpecialType; kind: PieceKind; beamAxis?: 'h' | 'v' }>;
} {
  const clearMap = new Map<string, Coord>();
  type Spec = { at: Coord; special: SpecialType; kind: PieceKind; beamAxis?: 'h' | 'v'; priority: number };
  const specials: Spec[] = [];

  const priority = (s: SpecialType): number => {
    switch (s) {
      case 'prism':
        return 4;
      case 'nova':
        return 3;
      case 'burst':
        return 2;
      case 'beam':
        return 1;
    }
  };

  for (const g of groups) {
    for (const cell of g.cells) {
      clearMap.set(`${cell.r},${cell.c}`, cell);
    }
    if (g.specialToCreate) {
      let at = g.specialAt ?? g.cells[0]!;
      if (preferredSpecialAt) {
        const hit = g.cells.find(
          (c) => c.r === preferredSpecialAt.r && c.c === preferredSpecialAt.c
        );
        if (hit) at = hit;
      }
      specials.push({
        at,
        special: g.specialToCreate,
        kind: g.kind,
        beamAxis: g.beamAxis,
        priority: priority(g.specialToCreate),
      });
    }
  }

  // One special per cell — keep highest priority
  const byCell = new Map<string, Spec>();
  for (const s of specials) {
    const k = `${s.at.r},${s.at.c}`;
    const prev = byCell.get(k);
    if (!prev || s.priority > prev.priority) byCell.set(k, s);
  }

  return {
    clearCells: [...clearMap.values()],
    specials: [...byCell.values()].map(({ priority: _p, ...rest }) => rest),
  };
}

/** Find any valid swap that creates a match. */
export function findValidMoves(grid: Grid): Array<{ a: Coord; b: Coord }> {
  const moves: Array<{ a: Coord; b: Coord }> = [];
  const tried = new Set<string>();

  grid.forEachActive((r, c) => {
    const neighbors = [
      { r, c: c + 1 },
      { r: r + 1, c },
    ];
    for (const n of neighbors) {
      if (!grid.active(n.r, n.c)) continue;
      if (!canSwapCells(grid, { r, c }, n)) continue;
      const key = `${r},${c}-${n.r},${n.c}`;
      if (tried.has(key)) continue;
      tried.add(key);
      if (swapCreatesMatch(grid, { r, c }, n)) {
        moves.push({ a: { r, c }, b: n });
      }
    }
  });
  return moves;
}

export function canSwapCells(grid: Grid, a: Coord, b: Coord): boolean {
  const ca = grid.get(a.r, a.c);
  const cb = grid.get(b.r, b.c);
  if (!ca?.active || !cb?.active) return false;
  if (!ca.piece || !cb.piece) return false;
  if (ca.sealed || cb.sealed) return false;
  if (ca.blocker?.type === 'crate' || cb.blocker?.type === 'crate') return false;
  // one-way constraints: can only move along direction when leaving cell
  if (ca.oneWay) {
    const dr = b.r - a.r;
    const dc = b.c - a.c;
    if (!matchesDir(ca.oneWay, dr, dc)) return false;
  }
  if (cb.oneWay) {
    const dr = a.r - b.r;
    const dc = a.c - b.c;
    if (!matchesDir(cb.oneWay, dr, dc)) return false;
  }
  return true;
}

function matchesDir(
  dir: 'up' | 'down' | 'left' | 'right',
  dr: number,
  dc: number
): boolean {
  switch (dir) {
    case 'up':
      return dr === -1 && dc === 0;
    case 'down':
      return dr === 1 && dc === 0;
    case 'left':
      return dr === 0 && dc === -1;
    case 'right':
      return dr === 0 && dc === 1;
  }
}

export function swapCreatesMatch(grid: Grid, a: Coord, b: Coord): boolean {
  // Special + special always valid
  const pa = grid.pieceAt(a.r, a.c);
  const pb = grid.pieceAt(b.r, b.c);
  if (pa?.special && pb?.special) return true;
  // Special swapped with normal activates (valid move)
  if (pa?.special || pb?.special) {
    // beam/nova/burst/prism can be activated by swapping with anything adjacent
    if (pa?.special || pb?.special) return true;
  }

  // Temporary swap
  grid.setPiece(a.r, a.c, pb);
  grid.setPiece(b.r, b.c, pa);
  const matches = findMatches(grid);
  // revert
  grid.setPiece(a.r, a.c, pa);
  grid.setPiece(b.r, b.c, pb);
  return matches.length > 0;
}

export function hasAnyMatch(grid: Grid): boolean {
  return findMatches(grid).length > 0;
}
